/**
 * Audio playground composable
 * Manages TTS and STT state and API calls
 */

import { ref, computed, nextTick } from 'vue';
import { useNotification } from '@asabdev/asab-ui';
import { useApiError } from '@/features/auth/composables/useApiError';
import { useAuthStore } from '@/stores/auth';
import { useSessionStorage } from './useSessionStorage';
import { createOpenAIClient } from '../lib/openai-client';
import type { Stream } from 'openai/streaming';
import type { TranscriptionStreamEvent } from 'openai/resources/audio/transcriptions';
import type { SpeechCreateParams } from 'openai/resources/audio/speech';

export function useAudioPlayground() {
  const authStore = useAuthStore();
  const { clearError, safeApiCall } = useApiError();
  const { showSuccess, showError } = useNotification();
  const { session, loadSession, saveSession, createEmptySession } = useSessionStorage();

  if (!session.value) {
    const loaded = loadSession();
    if (!loaded) {
      session.value = createEmptySession();
    }
  }

  // TTS State
  const ttsTextState = ref<string>(session.value?.audio.ttsText || '');
  const ttsSelectedModelState = ref<string>(session.value?.audio.ttsSelectedModel || '');
  const ttsVoiceState = ref<string>(session.value?.audio.ttsVoice || 'alloy');
  const ttsSpeedState = ref<number>(session.value?.audio.ttsSpeed ?? 1.0);
  const ttsResponseFormatState = ref<NonNullable<SpeechCreateParams['response_format']>>(
    session.value?.audio.ttsResponseFormat ?? 'mp3'
  );
  const ttsInstructionsState = ref<string>(session.value?.audio.ttsInstructions || '');
  const ttsLanguageCodeState = ref<string>(session.value?.audio.ttsLanguageCode || '');
  const ttsAudioUrl = ref<string | null>(null);
  const isTtsLoading = ref(false);

  // STT State
  type SttResponseFormat = 'json' | 'text' | 'srt' | 'verbose_json' | 'vtt';
  type SttChunkingStrategy =
    | 'auto'
    | {
        type: 'server_vad';
        prefix_padding_ms?: number;
        silence_duration_ms?: number;
        threshold?: number;
      }
    | null;

  const sttSelectedModelState = ref<string>(session.value?.audio.sttSelectedModel || '');
  const sttTranscriptionState = ref<string>(session.value?.audio.sttTranscription || '');
  const sttLanguageState = ref<string>(session.value?.audio.sttLanguage || '');
  const sttPromptState = ref<string>(session.value?.audio.sttPrompt || '');

  const initialSttResponseFormat = session.value?.audio.sttResponseFormat;
  const sttResponseFormatState = ref<SttResponseFormat>(
    initialSttResponseFormat === 'json' ||
      initialSttResponseFormat === 'text' ||
      initialSttResponseFormat === 'srt' ||
      initialSttResponseFormat === 'verbose_json' ||
      initialSttResponseFormat === 'vtt'
      ? initialSttResponseFormat
      : 'json'
  );

  const sttTemperatureState = ref<number>(session.value?.audio.sttTemperature ?? 0);
  const sttTimestampGranularitiesState = ref<Array<'word' | 'segment'>>(
    session.value?.audio.sttTimestampGranularities || []
  );
  const sttStreamState = ref<boolean>(session.value?.audio.sttStream ?? false);
  const sttChunkingStrategyState = ref<SttChunkingStrategy>(
    session.value?.audio.sttChunkingStrategy ?? null
  );
  const isSttLoading = ref(false);

  const ttsText = computed({
    get: () => ttsTextState.value,
    set: (value: string) => {
      ttsTextState.value = value;
      updateSession();
    },
  });

  const ttsSpeed = computed({
    get: () => ttsSpeedState.value,
    set: (value: number) => {
      ttsSpeedState.value = value;
      updateSession();
    },
  });

  const ttsResponseFormat = computed({
    get: () => ttsResponseFormatState.value,
    set: (value: NonNullable<SpeechCreateParams['response_format']>) => {
      ttsResponseFormatState.value = value;
      updateSession();
    },
  });

  const ttsInstructions = computed({
    get: () => ttsInstructionsState.value,
    set: (value: string) => {
      ttsInstructionsState.value = value;
      updateSession();
    },
  });

  const ttsLanguageCode = computed({
    get: () => ttsLanguageCodeState.value,
    set: (value: string) => {
      ttsLanguageCodeState.value = value;
      updateSession();
    },
  });

  const sttLanguage = computed({
    get: () => sttLanguageState.value,
    set: (value: string) => {
      sttLanguageState.value = value;
      updateSession();
    },
  });

  const sttPrompt = computed({
    get: () => sttPromptState.value,
    set: (value: string) => {
      sttPromptState.value = value;
      updateSession();
    },
  });

  const sttResponseFormat = computed({
    get: () => sttResponseFormatState.value,
    set: (value: SttResponseFormat) => {
      sttResponseFormatState.value = value;
      updateSession();
    },
  });

  const sttTemperature = computed({
    get: () => sttTemperatureState.value,
    set: (value: number) => {
      sttTemperatureState.value = value;
      updateSession();
    },
  });

  const sttStream = computed({
    get: () => sttStreamState.value,
    set: (value: boolean) => {
      sttStreamState.value = value;
      updateSession();
    },
  });

  const isStreamResponse = (response: unknown): response is Stream<TranscriptionStreamEvent> => {
    return typeof response === 'object' && response !== null && Symbol.asyncIterator in response;
  };

  const handleStreamingTranscription = async (
    stream: Stream<TranscriptionStreamEvent>
  ): Promise<void> => {
    let accumulatedText = '';

    for await (const event of stream) {
      if (event.type === 'transcript.text.delta') {
        accumulatedText += event.delta;
        sttTranscriptionState.value = accumulatedText;
        await nextTick();
      }

      if (event.type === 'transcript.text.done') {
        sttTranscriptionState.value = event.text;
        await nextTick();
      }
    }
  };

  /**
   * Generate speech from text using TTS API
   */
  const generateSpeech = async (text: string): Promise<void> => {
    if (!authStore.token || !ttsSelectedModelState.value) return;
    if (text.length > 4096) return;

    isTtsLoading.value = true;
    clearError();

    const result = await safeApiCall(
      async () => {
        const client = createOpenAIClient({
          token: authStore.token!,
          isApiKey: false,
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ttsParams: any = {
          model: ttsSelectedModelState.value,
          voice: ttsVoiceState.value,
          input: text,
          response_format: ttsResponseFormatState.value,
          speed: Math.min(4, Math.max(0.25, ttsSpeedState.value)),
        };
        if (ttsInstructionsState.value.trim()) {
          ttsParams.instructions = ttsInstructionsState.value.trim();
        }
        if (ttsLanguageCodeState.value.trim()) {
          // OpenAI-compatible extension honored by Gemini TTS.
          ttsParams.language_code = ttsLanguageCodeState.value.trim();
        }
        const response = await client.audio.speech.create(ttsParams as SpeechCreateParams);

        // Convert response to blob URL
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        // Revoke previous URL if exists
        if (ttsAudioUrl.value) {
          URL.revokeObjectURL(ttsAudioUrl.value);
        }

        ttsAudioUrl.value = url;
        return true;
      },
      {
        context: 'Generate Speech',
        retryOptions: { maxRetries: 2, initialDelay: 1000 },
      }
    );

    isTtsLoading.value = false;

    if (result) {
      showSuccess('Speech generated successfully');
      updateSession();
    } else {
      showError('Failed to generate speech');
    }
  };

  /**
   * Transcribe audio file using STT API
   */
  const transcribeAudio = async (file: File): Promise<void> => {
    if (!authStore.token || !sttSelectedModelState.value) return;

    isSttLoading.value = true;
    clearError();

    const result = await safeApiCall(
      async () => {
        const client = createOpenAIClient({
          token: authStore.token!,
          isApiKey: false,
        });

        const requestBody = {
          model: sttSelectedModelState.value,
          file: file,
          language: sttLanguageState.value.trim() ? sttLanguageState.value.trim() : undefined,
          prompt: sttPromptState.value.trim() ? sttPromptState.value.trim() : undefined,
          response_format: sttResponseFormatState.value,
          temperature: sttTemperatureState.value,
          timestamp_granularities:
            sttResponseFormatState.value === 'verbose_json' &&
            sttTimestampGranularitiesState.value.length > 0
              ? sttTimestampGranularitiesState.value
              : undefined,
          chunking_strategy: sttChunkingStrategyState.value ?? undefined,
        } as const;

        const response = sttStreamState.value
          ? await client.audio.transcriptions.create({ ...requestBody, stream: true })
          : await client.audio.transcriptions.create({ ...requestBody, stream: false });

        if (isStreamResponse(response)) {
          sttTranscriptionState.value = '';
          await handleStreamingTranscription(response);
          return true;
        }

        if (typeof response === 'string') {
          sttTranscriptionState.value = response;
          return true;
        }

        if (
          response &&
          typeof response === 'object' &&
          'text' in response &&
          typeof response.text === 'string'
        ) {
          sttTranscriptionState.value = response.text;
          return true;
        }

        sttTranscriptionState.value = '';
        return true;
      },
      {
        context: 'Transcribe Audio',
        retryOptions: { maxRetries: 2, initialDelay: 1000 },
      }
    );

    isSttLoading.value = false;

    if (result) {
      showSuccess('Audio transcribed successfully');
      updateSession();
    } else {
      showError('Failed to transcribe audio');
    }
  };

  const setTtsModel = (modelId: string): void => {
    ttsSelectedModelState.value = modelId;
    updateSession();
  };

  const setSttModel = (modelId: string): void => {
    sttSelectedModelState.value = modelId;
    updateSession();
  };

  const setTtsVoice = (voice: string): void => {
    ttsVoiceState.value = voice;
    updateSession();
  };

  const setTtsText = (text: string): void => {
    ttsTextState.value = text;
    updateSession();
  };

  const setSttTimestampGranularity = (granularity: 'word' | 'segment', enabled: boolean): void => {
    const current = new Set(sttTimestampGranularitiesState.value);
    if (enabled) current.add(granularity);
    else current.delete(granularity);
    sttTimestampGranularitiesState.value = Array.from(current);
    updateSession();
  };

  const setSttChunkingMode = (mode: 'none' | 'auto' | 'server_vad'): void => {
    if (mode === 'none') {
      sttChunkingStrategyState.value = null;
      updateSession();
      return;
    }

    if (mode === 'auto') {
      sttChunkingStrategyState.value = 'auto';
      updateSession();
      return;
    }

    if (!sttChunkingStrategyState.value || sttChunkingStrategyState.value === 'auto') {
      sttChunkingStrategyState.value = { type: 'server_vad' };
      updateSession();
      return;
    }

    if (
      typeof sttChunkingStrategyState.value === 'object' &&
      sttChunkingStrategyState.value.type === 'server_vad'
    ) {
      updateSession();
    }
  };

  const updateSttServerVadConfig = (config: {
    prefix_padding_ms?: number;
    silence_duration_ms?: number;
    threshold?: number;
  }): void => {
    if (!sttChunkingStrategyState.value || sttChunkingStrategyState.value === 'auto') {
      sttChunkingStrategyState.value = { type: 'server_vad', ...config };
      updateSession();
      return;
    }

    if (
      typeof sttChunkingStrategyState.value === 'object' &&
      sttChunkingStrategyState.value.type === 'server_vad'
    ) {
      sttChunkingStrategyState.value = { ...sttChunkingStrategyState.value, ...config };
      updateSession();
    }
  };

  const updateSession = (): void => {
    if (session.value) {
      session.value.audio = {
        ttsText: ttsTextState.value,
        ttsSelectedModel: ttsSelectedModelState.value,
        ttsVoice: ttsVoiceState.value,
        ttsSpeed: ttsSpeedState.value,
        ttsResponseFormat: ttsResponseFormatState.value,
        ttsInstructions: ttsInstructionsState.value,
        ttsLanguageCode: ttsLanguageCodeState.value,
        sttSelectedModel: sttSelectedModelState.value,
        sttTranscription: sttTranscriptionState.value,
        sttLanguage: sttLanguageState.value,
        sttPrompt: sttPromptState.value,
        sttResponseFormat: sttResponseFormatState.value,
        sttTemperature: sttTemperatureState.value,
        sttTimestampGranularities: sttTimestampGranularitiesState.value,
        sttStream: sttStreamState.value,
        sttChunkingStrategy: sttChunkingStrategyState.value,
      };
      session.value.timestamp = new Date();
      saveSession(session.value);
    }
  };

  return {
    // TTS
    ttsText,
    ttsSelectedModel: computed(() => ttsSelectedModelState.value),
    ttsVoice: computed(() => ttsVoiceState.value),
    ttsSpeed,
    ttsResponseFormat,
    ttsInstructions,
    ttsLanguageCode,
    ttsAudioUrl: computed(() => ttsAudioUrl.value),
    isTtsLoading: computed(() => isTtsLoading.value),

    // STT
    sttSelectedModel: computed(() => sttSelectedModelState.value),
    sttTranscription: computed(() => sttTranscriptionState.value),
    isSttLoading: computed(() => isSttLoading.value),
    sttLanguage,
    sttPrompt,
    sttResponseFormat,
    sttTemperature,
    sttTimestampGranularities: computed(() => sttTimestampGranularitiesState.value),
    sttStream,
    sttChunkingStrategy: computed(() => sttChunkingStrategyState.value),

    // Methods
    generateSpeech,
    transcribeAudio,
    setTtsModel,
    setSttModel,
    setTtsVoice,
    setTtsText,
    setSttTimestampGranularity,
    setSttChunkingMode,
    updateSttServerVadConfig,
  };
}
